<template>
  <div
    class="bg-surface-raised group flex flex-col gap-2 squircle-md rounded border"
    :class="{
      'min-w-56': linkedToNotification
    }"
  >
    <div class="flex flex-row items-center gap-3">
      <div
        class="relative flex aspect-square h-14 min-w-0 items-center justify-center overflow-hidden squircle-l-md rounded-l bg-primary-100"
      >
        <div
          v-if="!linkedToNotification"
          class="absolute inset-0 flex items-center justify-center"
          :class="{
            'opacity-0 group-hover:opacity-100': !alreadyAttachedAttachments
          }"
        >
          <button
            v-if="alreadyAttachedAttachments"
            class="flex size-6 items-center justify-center rounded-full border bg-red-500 text-white"
            @click="$emit('remove-attachment')"
          >
            <FontAwesomeIcon :icon="['fas', 'times']" size="xs" />
          </button>
          <button
            v-else
            class="flex size-6 items-center justify-center rounded-full border bg-primary text-contrast-primary"
            @click="$emit('add-attachment')"
          >
            <FontAwesomeIcon :icon="['fas', 'arrow-left']" size="xs" />
          </button>
        </div>
        <img
          v-if="imgValid"
          :src="imgSrc"
          class="inset-0 block aspect-square h-full w-full object-cover"
          :class="{ 'scale-125 blur-md': blurred }"
          @error="imgError = true"
        />
        <FontAwesomeIcon v-else :icon="['fal', 'file']" size="xl" />
      </div>
      <div
        class="flex min-w-0 flex-1 flex-col"
        :class="{
          'pr-3': !linkedToNotification || !canDeleteAttachment
        }"
      >
        <a
          v-if="attachment?.original_url"
          class="line-clamp-2 text-sm font-bold break-words"
          :href="attachment.original_url"
          :title="attachmentTitle"
          target="_blank"
        >
          {{ attachmentTitle }}
        </a>
        <h4
          v-else
          class="line-clamp-2 text-sm font-bold break-words"
          :title="attachmentTitle"
        >
          {{ attachmentTitle }}
        </h4>
        <button
          v-if="isSensitive"
          type="button"
          class="mt-1 flex min-w-0 flex-row items-center gap-1 text-xs text-gray-500"
          :title="revealed ? $t('button.hide') : $t('button.show')"
          @click.stop="revealed = !revealed"
        >
          <FontAwesomeIcon
            :icon="['fal', revealed ? 'eye-slash' : 'eye']"
            size="sm"
          />
          <span class="truncate">
            {{ $t('attributes.sensitive_content') }}
          </span>
        </button>
      </div>
      <div
        v-if="linkedToNotification && canDeleteAttachment"
        class="flex h-full items-center justify-center pr-3"
      >
        <BaseDropDownMenu
          :options="[
            {
              label: $t('button.delete'),
              icon: 'trash',
              gate: true,
              bg_color: 'bg-red-500',
              txt_color: 'text-white',
              action: () => $emit('remove-attachment')
            }
          ]"
          size="small"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import BaseDropDownMenu from '@c/addf-package/components/dropdown/BaseDropDownMenu.vue'

export default {
  name: 'AttachmentComponent',
  components: { BaseDropDownMenu, FontAwesomeIcon },
  props: {
    attachment: {
      type: Object,
      required: true
    },
    linkedToNotification: {
      type: Boolean,
      required: true
    },
    alreadyAttachedAttachments: {
      type: Boolean,
      required: false,
      default: false
    },
    canDeleteAttachment: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['remove-attachment', 'add-attachment'],
  data() {
    return {
      imgError: false,
      revealed: false
    }
  },
  computed: {
    /**
     * Read once and reused as the tooltip, so the full name is always exactly what the
     * two clamped lines are cutting short. Attachments carry their label under `name`
     * when they come from a media, under `title` when they were added by hand.
     */
    attachmentTitle() {
      return this.attachment.name ?? this.attachment.title
    },
    imgValid() {
      return this.imgSrc && !this.imgError
    },
    /**
     * The flag lives on the media itself. In the suggested-attachments sidebar
     * the media sits under `object`; once attached to the mail the component is
     * handed the media directly. Media copied into the mail's own
     * `email_attachments` collection do not carry it (the copy only keeps a
     * `type` property), so only the sidebar blurs today.
     */
    isSensitive() {
      const customProperties = this.linkedToNotification
        ? this.attachment.custom_properties
        : this.attachment.object?.custom_properties

      return customProperties?.sensitive_content === true
    },
    blurred() {
      return this.isSensitive && !this.revealed
    },
    imgSrc() {
      return this.linkedToNotification
        ? this.attachment.preview_url
        : this.attachment.object.preview_url
    }
  }
}
</script>

<style scoped></style>
